import { useState } from 'react';
import * as Sentry from '@sentry/react';
import { Download, MoreHorizontal, Pencil, Share2, Trash2 } from 'lucide-react';

import {
  conversationLabel,
  conversationPreview,
  conversationRecencyStamp,
  DeleteConversationModal,
  isConversationViewer,
  RenameConversationModal,
  useRenameConversationDialog,
} from '@/components/agent-chat';
import { ShareModal } from '@/components/share-modal';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ROUTES } from '@/constants/routes';
import { useOrgNavigate } from '@/hooks/use-org-navigate';
import {
  getAgentConversationMessages,
  type AgentConversationSummary,
} from '@/services/agents/config';
import { useLaunchDarkly } from '@/providers/launchdarkly-provider';
import { useOrgStore } from '@/stores/org-store';
import { downloadConversationMarkdown } from '@/utils/download-conversation';
import { formatTimeAgo } from '@/utils/format-date';

interface ChatCardProps {
  conversation: AgentConversationSummary;
  agentId: string;
  agentName?: string;
}

export function ChatCard({ conversation, agentId, agentName }: ChatCardProps) {
  const navigate = useOrgNavigate();
  const orgId = useOrgStore((s) => s.currentOrg?.orgid);

  const { isChatSharingEnabled } = useLaunchDarkly();
  const { renameTarget, openRename, renameProps } =
    useRenameConversationDialog<AgentConversationSummary>();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const isViewer = isConversationViewer(conversation.user_role);
  const canShare = isChatSharingEnabled && !isViewer;

  const title = conversationLabel(conversation);
  const preview = conversationPreview(conversation);
  const lastMessageAgo = formatTimeAgo(conversationRecencyStamp(conversation));
  const lastMessageLabel = lastMessageAgo
    ? `Last message ${lastMessageAgo}`
    : '';
  const sharedByLabel =
    isViewer && conversation.owner_email
      ? `Shared by ${conversation.owner_email}`
      : '';
  // Joined rather than interpolated: a chat with no timestamp would
  // otherwise render a dangling "Shared by … · ".
  const metaLabel = [sharedByLabel, lastMessageLabel]
    .filter(Boolean)
    .join(' · ');

  const handleOpen = () => {
    navigate(
      ROUTES.CHAT_HOME.CONVERSATION.replace(
        ':conversationId',
        conversation.conversation_id,
      ),
    );
  };

  const handleDownload = async () => {
    if (!orgId) return;
    try {
      const messages = await getAgentConversationMessages(
        orgId,
        agentId,
        conversation.conversation_id,
      );
      downloadConversationMarkdown(conversation, messages, { agentName });
    } catch (e) {
      Sentry.captureException(e, {
        extra: {
          context: 'chats-download-conversation',
          orgId,
          agentId,
          conversationId: conversation.conversation_id,
        },
      });
    }
  };

  const overflowMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant='ghost'
          size='icon'
          className='size-8 shrink-0'
          aria-label={`Chat options for ${title}`}
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal className='size-4' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align='end'
        className='w-48'
        onClick={(e) => e.stopPropagation()}
      >
        {!isViewer && (
          <DropdownMenuItem onSelect={() => openRename(conversation)}>
            <Pencil className='size-4' />
            Rename
          </DropdownMenuItem>
        )}
        {canShare && (
          <DropdownMenuItem onSelect={() => setShareOpen(true)}>
            <Share2 className='size-4' />
            Share
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={() => void handleDownload()}>
          <Download className='size-4' />
          Download
        </DropdownMenuItem>
        {!isViewer && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => setDeleteOpen(true)}>
              <Trash2 className='size-4' />
              Delete
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const dialogs = (
    <>
      {renameTarget && (
        <RenameConversationModal
          agentId={agentId}
          conversationId={renameTarget.conversation_id}
          currentTitle={conversation.title ?? ''}
          {...renameProps}
        />
      )}

      {deleteOpen && (
        <DeleteConversationModal
          agentId={agentId}
          conversationId={conversation.conversation_id}
          label={title}
          onClose={() => setDeleteOpen(false)}
        />
      )}

      {canShare && (
        <ShareModal
          resourceType='chat'
          resourceId={conversation.conversation_id}
          orgId={orgId ?? null}
          open={shareOpen}
          onOpenChange={setShareOpen}
        />
      )}
    </>
  );

  return (
    <>
      <div
        onClick={handleOpen}
        className='flex cursor-pointer flex-col gap-3 rounded-xl border border-border bg-card p-4 shadow-sm transition-shadow hover:shadow-md'
      >
        <div className='flex items-start justify-between gap-2'>
          <button
            type='button'
            onClick={(e) => {
              e.stopPropagation();
              handleOpen();
            }}
            className='min-w-0 flex-1 truncate text-left text-base font-medium text-foreground'
          >
            {title}
          </button>
          {overflowMenu}
        </div>
        <p className='line-clamp-2 min-h-10 text-sm text-muted-foreground'>
          {preview}
        </p>
        <span className='text-xs text-muted-foreground'>{metaLabel}</span>
      </div>
      {dialogs}
    </>
  );
}
